import { Observable, of } from 'rxjs';
import { mergeMap, filter, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';

// UI-Components
import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService } from 'ui-components/services/notification-service';

import { BULK_VOID_PIPELINE } from '../../../job-pipelines/bulk-void/bulk-void-pipeline';
import * as JobPipelineActions from '../../../store/job-pipeline/job-pipeline.actions';

// Store
import Global from '../../../store/global-selectors';
import * as Actions from '../../../store/bulk/void/bulk.void.actions';
import * as BulkVoidApi from '../../../store/bulk/void/bulk.void.api';

import { BulkPurchaseNotificationService } from '../../../services/bulk-purchase-noto-service';
import { PusherService } from '../../../services/pusher-service';

import { VoidLabelModal } from '../../../components/modals/void-label-modal';

const bulkVoidPusherEventNames = ['bulk-action:voidLabel'];

const internalShipmentMapper = (datum) => {
  const voided = get(datum, 'isVoided', false);
  return {
    ...datum,
    voided,
    isWarning: !voided,
    trackingInformation: datum.trackingInformation || {},
  };
};

export const startBulkVoid = (action$) => {
  return action$.pipe(
    ofType(Actions.BULK_VOID_START),
    mergeMap(({ fulfillmentIds = [] }) => {
      return of(
        JobPipelineActions.startJobPipeline(BULK_VOID_PIPELINE, {
          fulfillmentIds,
        }),
      );
    }),
  );
};

export const showBulkVoidConfirmation = (action$) =>
  action$.pipe(
    ofType(Actions.SHOW_BULK_VOID_CONFIRMATION),
    mergeMap(({ jobId }) => {
      return new Observable((observer) => {
        const modalId = ModalService.launchModal({
          component: VoidLabelModal,
          props: {
            jobId,
          },
          onCancel: () => {
            observer.next(JobPipelineActions.exitJobPipeline(jobId));
          },
          onClose: () => observer.complete(),
        });
        observer.next(JobPipelineActions.updateJob(jobId, { modalId }));
      }).pipe(mergeMap((next) => of(next)));
    }),
  );

export const sendBulkVoidRequest = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.REQUEST_BULK_VOID),
    mergeMap(({ jobId }) => {
      const jobData = Global.selectJobPipelineJobDataByJobId(getState(), { jobId }) || {};
      const { fulfillmentIds = [] } = jobData;
      return BulkVoidApi.bulkVoidLabelsByFulfillmentId({ fulfillmentIds }).pipe(
        mergeMap((data) => {
          const { jobGuids } = data;
          if (!jobGuids?.length) {
            BulkPurchaseNotificationService.showGenericHttpNotification();
            return of(JobPipelineActions.exitJobPipeline(jobId));
          }
          return new Observable((observer) => {
            const onMessage = (msg) => observer.next(msg);
            const allUnbindFromEvent = bulkVoidPusherEventNames.map((eventName) => PusherService.bindToEvent(eventName, onMessage));

            return () => {
              allUnbindFromEvent.forEach((unbindFromEvent) => unbindFromEvent());
            };
          }).pipe(
            filter((msg) => {
              return jobGuids.includes(msg.messageGuid);
            }),
            mergeMap(({ data: fulfillments }) => {
              const voidMessages = fulfillments.reduce((acc, fill) => {
                return fill ? acc.concat(internalShipmentMapper(fill)) : acc;
              }, []);
              return of(JobPipelineActions.updateJob(jobId, { voidMessages }), JobPipelineActions.goForwardOnJob(jobId));
            }),
          );
        }),
        catchError(() => {
          BulkPurchaseNotificationService.showGenericHttpNotification();
          return of(JobPipelineActions.exitJobPipeline(jobId));
        }),
      );
    }),
  );

export const launchBulkVoidResultsModal = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.LAUNCH_BULK_VOID_RESULTS_MODAL),
    mergeMap(({ jobId }) => {
      const jobData = Global.selectJobPipelineJobDataByJobId(getState(), {
        jobId,
      });
      const { notificationId } = jobData;
      NotificationService.removeNotification(notificationId);

      return new Observable((observer) => {
        const nextModalId = ModalService.launchModal({
          component: VoidLabelModal,
          props: {
            jobId,
            skipWarning: true,
            isLoading: true,
          },
          onCancel: () => {
            observer.next(JobPipelineActions.exitJobPipeline(jobId));
          },
          onClose: () => {
            observer.next(JobPipelineActions.exitJobPipeline(jobId));
            observer.complete();
          },
        });
        observer.next(JobPipelineActions.updateJob(jobId, { modalId: nextModalId }));
        observer.next(JobPipelineActions.goForwardOnJob(jobId));
      }).pipe(mergeMap((next) => of(next)));
    }),
  );

export const bulkVoidRetry = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.BULK_VOID_RETRY),
    mergeMap(({ jobId }) => {
      const jobData = Global.selectJobPipelineJobDataByJobId(getState(), {
        jobId,
      });
      const { fulfillmentIds } = jobData;

      return of(JobPipelineActions.exitJobPipeline(jobId), Actions.bulkVoidStart(fulfillmentIds));
    }),
  );
